// Replenish: top up pick faces and feed production lines. The list is what needs moving now, urgent first (a pick
// that would come up short, a line that runs out soon). A task is trips from reserve: go to the bin Courage
// suggests (or another with the item), scan it, the item and the lot, say how many, scan the bin being filled, and
// it's posted in Business Central as a bin move. Repeat until it's full, or finish short with a reason.
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { wms, type Content, type ReplDetail, type ReplTask } from "./wmsApi";
import { Alert, Done, Header, I, LocChip, ScanField, Stepper, feedback, fmt, errText, errBody, getLocation } from "./ui";

const clock = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
export function dueText(iso: string | null) {
  if (!iso) return "";
  const d = new Date(iso), now = new Date();
  if (d.getTime() < now.getTime()) return "late";
  if (d.toDateString() === now.toDateString()) return `due ${clock(iso)}`;
  return `due ${d.toLocaleDateString([], { weekday: "short" })} ${clock(iso)}`;
}
/** Why this bin needs stock, in a line. */
export function whyText(t: ReplTask) {
  const d = t.detail;
  if (t.reason === "pick_waiting") return `Picks need ${fmt(d.pickDemand ?? 0)}, bin has ${fmt(d.onHand ?? 0)}`;
  if (t.reason === "below_min") return `Has ${fmt(d.onHand ?? 0)} · min ${fmt(d.min ?? 0)}, max ${fmt(d.max ?? 0)}`;
  const o = d.orders ?? [];
  return [o.length ? `Feeds ${o[0].no}${o.length > 1 ? ` and ${o.length - 1} more` : ""}` : "Production feed", dueText(t.dueAt)].filter(Boolean).join(" · ");
}

export function ReplenListPage({ loc }: { loc: string }) {
  const [data, setData] = useState<Awaited<ReturnType<typeof wms.replen>> | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { wms.replen(loc).then(setData).catch((e) => setErr(errText(e))); }, [loc]);
  const urgent = data?.tasks.filter((t) => t.priority === 1) ?? [], rest = data?.tasks.filter((t) => t.priority !== 1) ?? [];
  const card = (t: ReplTask) => (
    <Link key={t.id} to={`/wms/replen/${t.id}`} className={`w-pickcard ${t.mine ? "started" : ""}`}>
      <span className="w-label-row">
        <span className="w-repl-to"><span className="w-small">Fill</span><span className="w-mono big">{t.toBin}</span></span>
        <span className={`w-tag ${t.reason === "pick_waiting" ? "bad" : t.reason === "line_feed" ? "feed" : ""}`}>{t.reasonLabel}</span>
      </span>
      <span className="w-row-main"><span className="w-strong">{t.itemNo}{t.description ? ` · ${t.description}` : ""}</span><span className="w-muted">{whyText(t)}</span></span>
      <span className="w-label-row">
        <span><span className="w-strong">Bring {fmt(t.remaining)} {t.uom}</span>
          {t.from ? <span className="w-muted"> from <span className="w-mono">{t.from}</span></span> : <span className="w-repl-warn"> · none in reserve</span>}</span>
        {t.mine ? <span className="w-link">Yours</span> : t.assignedTo ? <span className="w-small">{t.assignedTo} is on it</span> : null}
      </span>
      {t.reserve && t.reserve.short > 0 && t.from && <span className="w-repl-warn">Reserve has {fmt(t.reserve.available)} of {fmt(t.remaining)}</span>}
      {t.movedQty > 0 && <span className="w-progress"><span className="w-bar"><span style={{ width: `${Math.min(100, Math.round((t.movedQty / t.qty) * 100))}%` }} /></span><span className="w-small w-strong">{fmt(t.movedQty)} of {fmt(t.qty)}</span></span>}
    </Link>
  );
  return (
    <>
      <Header title="Replenish" sub={data ? (data.tasks.length ? `${data.tasks.length} bin${data.tasks.length === 1 ? "" : "s"} to fill` : "All bins stocked") : undefined} back="/wms" right={<LocChip code={loc} />} />
      <div className="w-body">
        {err && <Alert title="Couldn't load replenishment">{err}</Alert>}
        {!data && !err && <p className="w-muted">Checking bins at {loc}…</p>}
        {data && data.feed.on && !data.feed.available && <Alert tone="warn" title="Production feed is off">Business Central needs the Courage WMS extension 1.1 to show production components. Pick faces still work.</Alert>}
        {data && !data.tasks.length && (
          <div className="w-card w-empty">
            {data.rules || data.feed.available
              ? `Nothing needs filling at ${loc}. Bins with rules are above their min${data.feed.on && data.feed.available ? `, and lines have what production needs for the next ${data.feed.horizonHours} hours` : ""}.`
              : "No bins have min and max levels yet. A lead sets them in courageMFG under Inventory, Replenishment."}
          </div>
        )}
        {urgent.length > 0 && <div className="w-label">Urgent · {urgent.length}</div>}
        {urgent.map(card)}
        {rest.length > 0 && <div className="w-label">To do · {rest.length}</div>}
        {rest.map(card)}
      </div>
    </>
  );
}

type Phase = "load" | "taken" | "from" | "item" | "lot" | "qty" | "to" | "short" | "done";

export function ReplenTaskPage() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const loc = getLocation();
  const [d, setD] = useState<ReplDetail | null>(null);
  const [phase, setPhase] = useState<Phase>("load");
  const [takenBy, setTakenBy] = useState<string | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [fromBin, setFromBin] = useState<string | null>(null);
  const [rows, setRows] = useState<Content[]>([]);
  const [lot, setLot] = useState("");
  const [serial, setSerial] = useState("");
  const [qty, setQty] = useState(0);
  const [reason, setReason] = useState("reserve_empty");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [trips, setTrips] = useState<{ from: string; qty: number; lot: string }[]>([]);

  const begin = async (takeOver = false) => {
    setLoadErr(null);
    try {
      const r = await wms.replenStart(id, takeOver);
      setD(r); resetTrip(); setPhase("from");
    } catch (e) {
      const b = errBody(e);
      if (b.code === "taken") { setTakenBy(b.assignedTo ?? "Someone else"); setPhase("taken"); return; }
      if (b.code === "closed") { const r = await wms.replenTask(id).catch(() => null); if (r) { setD(r); setPhase("done"); return; } }
      setLoadErr(errText(e));
    }
  };
  useEffect(() => { begin(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  function resetTrip() { setFromBin(null); setRows([]); setLot(""); setSerial(""); setErr(null); }

  const t = d?.task;
  const unit = t?.uom ?? "";
  const mineHere = rows.filter((r) => t && r.itemNo === t.itemNo && r.variantCode === t.variantCode);
  const tracking: "lot" | "serial" | null = mineHere.some((r) => r.serialNo) ? "serial" : mineHere.some((r) => r.lotNo) ? "lot" : null;
  const availHere = mineHere.filter((r) => (!lot || r.lotNo === lot) && (!serial || r.serialNo === serial)).reduce((a, r) => a + r.qty, 0);
  const suggested = d?.sources[0] ?? null;

  async function scanFrom(code: string) {
    if (!t) return;
    setErr(null);
    const bin = code.trim().toUpperCase();
    if (bin === t.toBin) { feedback.bad(); setErr(`${bin} is the bin you're filling. Go to reserve.`); return; }
    setBusy(true);
    try {
      const r = await wms.bin(t.location, bin);
      const here = r.contents.filter((x) => x.itemNo === t.itemNo && x.variantCode === t.variantCode);
      if (!here.length) { feedback.bad(); setErr(`${r.bin.code} has no ${t.itemNo}.`); return; }
      feedback.ok(); setFromBin(r.bin.code); setRows(r.contents); setPhase("item");
    } catch (e) { feedback.bad(); setErr(errText(e)); } finally { setBusy(false); }
  }
  async function scanItem(code: string) {
    if (!t) return;
    setBusy(true); setErr(null);
    try {
      const r = await wms.scan(t.location, code);
      if (r.kind === "item" && r.item.itemNo === t.itemNo) {
        feedback.ok();
        if (tracking) {
          const s = d?.sources.find((x) => x.binCode === fromBin && (x.lotNo || x.serialNo));
          if (s && tracking === "lot") setLot(s.lotNo);
          setPhase("lot");
        } else { setQty(Math.min(t.remaining, availHere)); setPhase("qty"); }
        return;
      }
      feedback.bad(); setErr(r.kind === "item" ? `That's ${r.item.itemNo}. This task is ${t.itemNo}.` : `${code} isn't ${t.itemNo}.`);
    } catch (e) { feedback.bad(); setErr(errText(e)); } finally { setBusy(false); }
  }
  function chooseLot(v: string) {
    if (!t) return;
    const val = v.trim().toUpperCase();
    const row = mineHere.find((r) => (tracking === "serial" ? r.serialNo : r.lotNo).toUpperCase() === val);
    if (!row) { feedback.bad(); setErr(`${fromBin} has no ${t.itemNo} with ${tracking} ${val}.`); return; }
    feedback.ok(); setErr(null);
    if (tracking === "serial") { setSerial(row.serialNo); setLot(""); setQty(1); }
    else { setLot(row.lotNo); setSerial(""); setQty(Math.min(t.remaining, mineHere.filter((r) => r.lotNo === row.lotNo).reduce((a, r) => a + r.qty, 0))); }
    setPhase("qty");
  }
  async function scanTo(code: string) {
    if (!t || !fromBin) return;
    const bin = code.trim().toUpperCase();
    if (bin !== t.toBin) { feedback.bad(); setErr(`You scanned ${bin}. Put it in ${t.toBin}.`); return; }
    setBusy(true); setErr(null);
    try {
      const r = await wms.replenMove(t.id, { fromBin, lotNo: lot || undefined, serialNo: serial || undefined, qty });
      setTrips((x) => [...x, { from: fromBin, qty, lot: lot || serial }]);
      setD(r); resetTrip();
      if (r.task.status === "done") { feedback.done(); setPhase("done"); } else { feedback.ok(); setPhase("from"); }
    } catch (e) { feedback.bad(); setErr(errText(e)); } finally { setBusy(false); }
  }
  async function finishShort() {
    if (!t) return;
    setBusy(true); setErr(null);
    try {
      await wms.replenFinish(t.id, { shortReason: reason, fromBin: fromBin ?? suggested?.binCode ?? trips.at(-1)?.from });
      feedback.ok();
      const r = await wms.replenTask(t.id); setD(r); setPhase("done");
    } catch (e) { feedback.bad(); setErr(errText(e)); } finally { setBusy(false); }
  }
  async function leave() {
    if (!t) return;
    await wms.replenRelease(t.id).catch(() => {});
    nav("/wms/replen");
  }

  if (loadErr) return <><Header title="Replenish" back="/wms/replen" /><div className="w-body"><Alert title="Can't open this task">{loadErr}</Alert><Link to="/wms/replen" className="w-btn">Back to the list</Link></div></>;
  if (phase === "taken") return (
    <>
      <Header title="Replenish" back="/wms/replen" />
      <div className="w-body"><Alert tone="warn" title={`${takenBy} started this one`}>Take it over only if they've stopped. What they've moved still counts.</Alert></div>
      <div className="w-foot"><button type="button" className="w-btn primary" onClick={() => begin(true)}>Take it over</button><Link to="/wms/replen" className="w-btn">Back to the list</Link></div>
    </>
  );
  if (!d || !t) return <><Header title="Replenish" back="/wms/replen" /><div className="w-body"><p className="w-muted">Opening the task…</p></div></>;

  const header = (
    <>
      <Header title={`Fill ${t.toBin}`} sub={`${t.itemNo} · ${t.reasonLabel}`} back="/wms/replen" right={<LocChip code={t.location || loc} />} />
      <div className="w-progress-head">
        <span className="w-progress"><span className="w-bar"><span style={{ width: `${Math.min(100, Math.round((t.movedQty / Math.max(t.qty, 1e-9)) * 100))}%` }} /></span>
          <span className="w-small w-strong">{fmt(t.movedQty)} of {fmt(t.qty)} {unit}</span></span>
      </div>
    </>
  );

  if (phase === "done") {
    const short = t.status === "short";
    return (
      <>
        {header}
        <div className="w-body center">
          <span className={`w-bigtick ${short ? "warn" : ""}`}><I n={short ? "warn" : "check"} s={60} w={2.6} /></span>
          <h1 className="w-h1">{short ? `Moved ${fmt(t.movedQty)} of ${fmt(t.qty)}` : `${t.toBin} is filled`}</h1>
          <p className="w-muted">{short ? `${d.shortReasons[t.shortReason ?? ""] ?? "Short"}. ` : ""}{fmt(t.movedQty)} {unit} of {t.itemNo} moved in Business Central{trips.length > 1 ? ` in ${trips.length} trips` : ""}.</p>
          {trips.length > 0 && <div className="w-card w-rows" style={{ alignSelf: "stretch", textAlign: "left" }}>
            {trips.map((x, i) => <div key={i} className="w-line"><span className="w-mono">{x.from} → {t.toBin}</span><span className="w-row-main"><span className="w-muted">{x.lot}</span></span><span className="w-strong">{fmt(x.qty)} {unit}</span></div>)}
          </div>}
        </div>
        <div className="w-foot"><button type="button" className="w-btn primary" onClick={() => nav("/wms/replen")}>Next task <I n="arrow" /></button></div>
      </>
    );
  }

  const taskCard = (
    <div className="w-card w-item col">
      <div className="w-item-top"><span className="w-item-i"><I n="box" s={28} w={1.8} /></span>
        <span className="w-row-main"><span className="w-strong big">{t.itemNo}</span><span className="w-muted">{t.description}</span></span></div>
      <div className="w-two">
        <div className="w-stat soft"><span className="w-small">Still to bring</span><span className="w-statn">{fmt(t.remaining)} <small>{unit}</small></span></div>
        <div className="w-stat soft"><span className="w-small">In {t.toBin} now</span><span className="w-statn">{fmt(t.onHand ?? 0)} <small>{unit}</small></span></div>
      </div>
      <span className="w-small">{whyText(t)}</span>
    </div>
  );
  const checks = fromBin && (
    <div className="w-card w-rows">
      <Done label="From" value={fromBin} mono />
      {phase !== "item" && <Done label="Item" value={t.itemNo} />}
      {(lot || serial) && phase !== "lot" && <Done label={serial ? "Serial" : "Lot"} value={serial || lot} mono />}
      {phase === "to" && <Done label="Quantity" value={`${fmt(qty)} ${unit}`} />}
    </div>
  );

  return (
    <>
      {header}
      {phase === "from" && (
        <>
          <div className="w-body">
            {suggested ? (
              <div className="w-go-card">
                <span className="w-go-l">Go to bin</span>
                <span className="w-go-c">{suggested.binCode}</span>
                <span className="w-go-s">Take {fmt(suggested.qty)} {unit}{suggested.lotNo ? ` · lot ${suggested.lotNo}` : ""}{suggested.zone ? ` · zone ${suggested.zone}` : ""}</span>
              </div>
            ) : <Alert tone="warn" title="Nothing in reserve">Business Central shows no {t.itemNo} in a bin it can come from. Scan a bin if you know where some is, or finish short.</Alert>}
            {d.reserve.short > 0 && suggested && <Alert tone="warn" title={`Reserve has ${fmt(d.reserve.available)} of ${fmt(t.remaining)}`}>Bring what there is, then finish short.</Alert>}
            {taskCard}
            {d.sources.length > 1 && (
              <div className="w-section">
                <div className="w-label">Then</div>
                <div className="w-card w-rows">
                  {d.sources.slice(1).map((s, i) => <div key={i} className="w-line"><span className="w-mono w-binc">{s.binCode}</span><span className="w-row-main"><span className="w-muted">{s.lotNo ? `Lot ${s.lotNo}` : s.zone}</span></span><span className="w-strong">{fmt(s.qty)} {unit}</span></div>)}
                </div>
              </div>
            )}
            {err && <Alert title="Not that bin">{err}</Alert>}
          </div>
          <div className="w-foot">
            <ScanField label={suggested ? `Scan bin ${suggested.binCode}, or another with ${t.itemNo}` : `Scan a bin with ${t.itemNo}`} onScan={scanFrom} busy={busy} />
            <div className="w-two">
              <button type="button" className="w-btn" onClick={leave}>Leave for later</button>
              <button type="button" className="w-btn" onClick={() => { setErr(null); setPhase("short"); }}>Can't fill it all</button>
            </div>
          </div>
        </>
      )}
      {phase === "item" && (
        <>
          <div className="w-body">{checks}{taskCard}{err && <Alert title="Wrong item">{err}</Alert>}</div>
          <div className="w-foot"><ScanField label={`Scan the item: ${t.itemNo}`} onScan={scanItem} busy={busy} /><button type="button" className="w-btn" onClick={() => { resetTrip(); setPhase("from"); }}>Different bin</button></div>
        </>
      )}
      {phase === "lot" && (
        <>
          <div className="w-body">
            {checks}
            <div className="w-label">{tracking === "serial" ? "Serial numbers" : "Lots"} of {t.itemNo} in {fromBin}{tracking === "lot" ? ", oldest first" : ""}</div>
            <div className="w-list">
              {mineHere.map((r, i) => {
                const v = tracking === "serial" ? r.serialNo : r.lotNo;
                return (
                  <button key={i} type="button" className={`w-row-btn ${v === lot ? "sel" : ""}`} onClick={() => chooseLot(v)}>
                    <span className="w-row-main"><span className="w-strong w-mono">{v}</span>{v === lot && <span className="w-small">Suggested</span>}</span>
                    <span className="w-strong">{fmt(r.qty)} {unit}</span>
                  </button>
                );
              })}
            </div>
            {err && <Alert title="Not that one">{err}</Alert>}
          </div>
          <div className="w-foot"><ScanField label={tracking === "serial" ? "Scan the serial number" : "Scan the lot, or tap it"} onScan={chooseLot} busy={busy} /></div>
        </>
      )}
      {phase === "qty" && (
        <>
          <div className="w-body">
            {checks}
            <div className="w-card w-qtycard">
              <label htmlFor="repl-qty" className="w-label">How many are you taking?</label>
              <Stepper id="repl-qty" value={qty} onChange={setQty} max={serial ? 1 : Math.min(t.remaining, availHere)} of={`of ${fmt(t.remaining)}`} big />
              <span className="w-small">{fromBin} has {fmt(availHere)} {unit}{lot ? ` of lot ${lot}` : ""}.</span>
            </div>
            {err && <Alert title="Try again">{err}</Alert>}
          </div>
          <div className="w-foot">
            <button type="button" className="w-btn primary" disabled={!(qty > 0)} onClick={() => { setErr(null); setPhase("to"); }}>Take {fmt(qty)} to {t.toBin} <I n="arrow" /></button>
          </div>
        </>
      )}
      {phase === "to" && (
        <>
          <div className="w-body">
            <div className="w-go-card">
              <span className="w-go-l">Put it in</span>
              <span className="w-go-c">{t.toBin}</span>
              <span className="w-go-s">{fmt(qty)} {unit} of {t.itemNo}</span>
            </div>
            {checks}
            {err && <Alert title="Not moved">{err}</Alert>}
          </div>
          <div className="w-foot">
            <ScanField label={`Scan bin ${t.toBin} to post the move`} onScan={scanTo} busy={busy} />
            <button type="button" className="w-btn" onClick={() => setPhase("qty")}>Back</button>
          </div>
        </>
      )}
      {phase === "short" && (
        <>
          <div className="w-body">
            <Alert tone="warn" title={t.movedQty > 0 ? `Moved ${fmt(t.movedQty)} of ${fmt(t.qty)}` : "Nothing moved yet"}>{fmt(t.remaining)} {unit} won't get to {t.toBin} on this task.</Alert>
            <fieldset className="w-reasons">
              <legend className="w-label">Why?</legend>
              {Object.entries(d.shortReasons).map(([k, label]) => (
                <label key={k} className={reason === k ? "sel" : ""}><input type="radio" name="reason" checked={reason === k} onChange={() => setReason(k)} />{label}</label>
              ))}
            </fieldset>
            {(reason === "reserve_empty" || reason === "cant_find") && (fromBin ?? suggested?.binCode) && <p className="w-small">This flags {fromBin ?? suggested?.binCode} for a count.</p>}
            {err && <Alert title="Not saved">{err}</Alert>}
          </div>
          <div className="w-foot">
            <button type="button" className="w-btn primary" onClick={finishShort} disabled={busy}>Finish short</button>
            <button type="button" className="w-btn" onClick={() => setPhase("from")}>Back</button>
          </div>
        </>
      )}
    </>
  );
}
